import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';

/**
 * "Plan: X" badge. Presentation only — pass the raw plan key from entitlements
 * (`free` | `basic` | `medium` | `pro`); the label is localized via
 * `plans.planNames.<key>` with a raw-key fallback.
 *
 * With `showUpgrade` enabled, a free plan also renders an "upgrade" CTA that
 * emits `upgrade` (the parent decides where it leads — see UpgradeService).
 */
@Component({
  selector: 'app-plan-badge',
  standalone: true,
  imports: [CommonModule, TranslocoModule],
  templateUrl: './plan-badge.component.html',
  styleUrl: './plan-badge.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlanBadgeComponent {
  readonly plan = input<string | null>(null);
  readonly showUpgrade = input<boolean>(false);

  readonly upgrade = output<void>();

  private readonly transloco = inject(TranslocoService);

  /** Free (or not-yet-loaded) plans are the ones we nudge to upgrade. */
  readonly isFree = computed(() => {
    const plan = this.plan();
    return !plan || plan === 'free';
  });

  readonly label = computed(() => {
    const plan = this.plan() ?? 'free';
    const key = `plans.planNames.${plan}`;
    const translated = this.transloco.translate(key);
    return translated === key ? plan : translated;
  });
}
